/**
 * Secondary Vedic birth-moment checks — PLAN.md §6 ("Use secondary Vedic checks, not as the
 * main proof"). Every formula here is taken verbatim from `source_learning`:
 *
 *   - PRANAPADA (Vol_01 §9.3): birth time since sunrise in Vighatis converted to degrees
 *     (1 vighati = 24 s = 2°, i.e. one full circle per 72 min — a ~6-minute-per-sign, genuinely
 *     FINE discriminator), added to the natal Sun with the sign-type adjustment (movable +0°,
 *     fixed +240°, dual +120°). "An auspicious birth occurs when Pranapad falls in the 2nd,
 *     4th, 5th, 9th, 10th, or 11th house from the Ascendant" — and per
 *     01_sensitive_points_timing.md ("Pranapad — Auspicious-Birth Sensitive Point") "In any
 *     other Bhava, Pranapad indicates an inauspicious birth". The credit is GRADED by distance
 *     to the nearest verdict-flip house boundary and TWO-SIDED on the profile's delivery_ease
 *     birth fact (a known difficult delivery expects the inauspicious houses) — see
 *     graded_pranapada_score.
 *   - SPECIAL LAGNAS (Vol_01 §3.3): Hora Lagna (Sun-at-sunrise + ghatis/2.5 signs — wealth),
 *     Ghati Lagna (+ 1 sign per ghati — power/status/fame), Bhava Lagna (+ ghatis/5 signs —
 *     houses/life areas). "Keep all natal planetary positions fixed, but rebuild the house
 *     structure using each special Lagna as the first house" for its topic.
 *   - GANDANTA flags (Vol_12 §4.1, Vol_01 §6.2): the three water→fire junctions. Lagna
 *     Gandanta = the last/first half-Ghatika of rising (≈3° of the Ascendant either side);
 *     Nakshatra Gandanta = the Moon within ~2 Ghatikas of mean motion (≈0.45°) of the
 *     Revati/Ashvini, Aslesha/Magha, Jyeshtha/Mula junctions. These are INFORMATIONAL flags
 *     (they mark an inauspicious/boundary birth, not a wrong time): a Moon Gandanta also
 *     warns that the Vimshottari balance flips across that boundary.
 *   - TITHI GANDANTA flag (12_gandanta_births.md): "Last 2 Ghatikas of Purna Tithi (5th, 10th,
 *     or 15th Tithi), OR First 2 Ghatikas of Nanda Tithi (1st, 6th, or 11th Tithi)". Every
 *     Purna→Nanda boundary (5→6, 10→11, 15→1 in each paksha) is a Sun-Moon elongation multiple
 *     of 60°, so the flag reduces to elongation-within-orb of a 60° boundary. Display-only.
 *   - ABHUKTA MULA flag (12_gandanta_births.md): "Last 6 Ghatikas of Jyeshtha Nakshatra PLUS
 *     First 8 Ghatikas of Mula" — the "Most severe of ALL Gandanta periods", an ASYMMETRIC
 *     inner window of the Jyeshtha/Mula (240°) junction only. Display-only.
 *   - KALA VELA UPAGRAHAS (01_sensitive_points_timing.md): the day (sunrise→sunset; for night
 *     births sunset→next sunrise) split into 8 equal parts, the 8th lord-less, parts 1-7
 *     assigned to the planets in vaara order from the weekday lord (night: from the lord of
 *     the 5th weekday). Each upagraha = the ASCENDANT degree rising at the START of its
 *     planet's portion — Gulik(Mandi)=Saturn, Kaal=Sun, Mrityu=Mars, Ardhaprahar=Mercury,
 *     Yamaghantak=Jupiter. Display-only: the house each occupies from the candidate Lagna
 *     flips with the rising sign, and "Gulik afflicting the Ascendant" is the classically
 *     flagged malefic case (`gulika_in_lagna`).
 *   - VARNAD LAGNA (10_varnad_lagna.md): derived from the natal Lagna and Hora Lagna by the
 *     classical odd-forward-from-Aries / even-reverse-from-Pisces counts (the old
 *     "repeat-the-count from Hora Lagna" shortcut is explicitly rejected there). Display-only
 *     longevity sensitive point.
 *
 * Per PLAN §6 these corroborate, never decide: the Pranapada check enters scoring as a LIGHT
 * confirmation (50–100, never penalising, small weight), the Ghati-Lagna check confirms
 * status/fame events only, and the Gandanta flags are display-only.
 */

import * as swe from "../astro/ephemeris";
import { SIGN_RULERS } from "../astro/constants";
import { ensure_swiss_ephemeris, get_house_from_signs, local_to_jd } from "../astro/chart";
import { sunrise_jd_ut } from "../astro/kp";
import type { RawChartData } from "../astro/models";
import type { PlainDate, PlainDateTime } from "./datetime";
import { addDays, combine, dateOnly, weekday } from "./datetime";
import { pymod, pythonRound } from "./pyutil";

/** Vol_01 §9.3: the houses (from the Ascendant) where Pranapada marks an auspicious birth. */
export const PRANAPADA_AUSPICIOUS_HOUSES = new Set([2, 4, 5, 9, 10, 11]);

/** The complement — 01_sensitive_points_timing.md ("Pranapad — Auspicious-Birth Sensitive
 *  Point"): "In any other Bhava, Pranapad indicates an inauspicious birth." For a birth KNOWN
 *  to have been difficult/complicated (`delivery_ease` trait), THESE are the expected houses,
 *  so the tie-break credit flips to them (see graded_pranapada_score). */
export const PRANAPADA_INAUSPICIOUS_HOUSES = new Set([1, 3, 6, 7, 8, 12]);

/** Grading ramp for the Pranapada credit, in degrees of Pranapada arc. Pranapada advances 2°
 *  per vighati (24 s) = 5°/minute, so 10° ≈ 2 minutes of birth time: a candidate whose
 *  Pranapada sits ≥ 2 min of birth time away from a verdict-flip boundary gets full credit;
 *  closer than that the credit falls off linearly to ~0 AT the boundary — the boundary is
 *  exactly where a few-minutes birth-time shift flips the auspicious/inauspicious verdict,
 *  so the gradient is the fine discriminator this layer contributes. */
export const PRANAPADA_GRADING_RAMP_DEG = 10.0;

// Sign-type adjustment (Vol_01 §9.3): movable (chara) +0°, fixed (sthira) +240°, dual +120°.
const _SIGN_TYPE_ADJUSTMENT_DEG = [0.0, 240.0, 120.0]; // index = sign % 3 (Aries=0 movable)

const _NORM = (deg: number): number => pymod(deg, 360.0);

/** Whole-sign house (1-12) of a longitude counted from an ascendant sign. */
function _whole_sign_house_of(longitude: number, asc_sign: number): number {
  return get_house_from_signs(Math.trunc(_NORM(longitude) / 30.0), Math.trunc(asc_sign));
}

export interface SunriseAnchor {
  available: boolean;
  /** JD(UT) of the sunrise the birth belongs to (the previous one for pre-sunrise births). */
  sunrise_jd: number | null;
  /** Sidereal Sun longitude at that sunrise. */
  sun_at_sunrise: number | null;
  /** Time from sunrise to the birth moment, in seconds (≥ 0). */
  seconds_since_sunrise: number | null;
  /** Local calendar date of that sunrise — the VEDIC day the birth belongs to (drives the vaara lord). */
  vedic_date: PlainDate | null;
}

// Sunrise + Sun-at-sunrise depend only on the DATE and the place, but the scans call this for
// every candidate second of the same day — memoise both (the cache stays tiny: one entry per
// scanned date/place).
const _rise_cache = new Map<string, number | null>();
const _sun_cache = new Map<number, number>();

function _cached_rise(midnight_jd: number, lat: number, lon: number): number | null {
  const key = `${midnight_jd.toFixed(6)}|${lat}|${lon}`;
  if (!_rise_cache.has(key)) _rise_cache.set(key, sunrise_jd_ut(midnight_jd, lat, lon));
  return _rise_cache.get(key)!;
}

function _cached_sun_at(jd: number): number {
  const key = pythonRound(jd, 8);
  if (!_sun_cache.has(key)) {
    const [sun_res] = swe.calc_ut(jd, swe.SUN, swe.FLG_SIDEREAL);
    _sun_cache.set(key, _NORM(sun_res[0]));
  }
  return _sun_cache.get(key)!;
}

/**
 * The sunrise the birth moment belongs to (Vedic days run sunrise→sunrise, so a pre-sunrise
 * birth anchors to the PREVIOUS day's sunrise) plus the sidereal Sun there. Returns
 * available:false at polar latitudes / ephemeris failure — callers then skip the checks.
 */
export function sunrise_anchor(
  birth_dt: PlainDateTime,
  lat: number,
  lon: number,
  timezone_offset: number,
): SunriseAnchor {
  const none: SunriseAnchor = {
    available: false, sunrise_jd: null, sun_at_sunrise: null, seconds_since_sunrise: null,
    vedic_date: null,
  };
  try {
    ensure_swiss_ephemeris();
    const birth_jd = local_to_jd(birth_dt, timezone_offset);
    const midnight = combine(dateOnly(birth_dt), { hour: 0, minute: 0, second: 0 });
    let vedic_date = dateOnly(birth_dt);
    let rise = _cached_rise(local_to_jd(midnight, timezone_offset), lat, lon);
    if (rise === null) return none;
    if (rise > birth_jd) {
      // Born before today's sunrise → the Vedic day began at YESTERDAY's sunrise.
      rise = _cached_rise(local_to_jd(midnight, timezone_offset) - 1.0, lat, lon);
      if (rise === null || rise > birth_jd) return none;
      vedic_date = addDays(vedic_date, -1);
    }
    return {
      available: true,
      sunrise_jd: rise,
      sun_at_sunrise: _cached_sun_at(rise),
      seconds_since_sunrise: (birth_jd - rise) * 86400.0,
      vedic_date,
    };
  } catch {
    return none;
  }
}

/**
 * Pranapada longitude (Vol_01 §9.3). `sun_longitude` is the NATAL (birth-moment) sidereal Sun;
 * `seconds_since_sunrise` comes from sunrise_anchor. 1 vighati = 24 s = 2°.
 */
export function pranapada_longitude(sun_longitude: number, seconds_since_sunrise: number): number {
  const vighatis = seconds_since_sunrise / 24.0;
  const sun_sign = Math.trunc(_NORM(sun_longitude) / 30.0);
  const adjustment = _SIGN_TYPE_ADJUSTMENT_DEG[pymod(sun_sign, 3)];
  return _NORM(sun_longitude + vighatis * 2.0 + adjustment);
}

// ── Graded, two-sided Pranapada credit ──────────────────────────────────────────────────────

export type DeliveryEase = "easy" | "difficult";

/**
 * DEFENSIVE read of the `delivery_ease` trait from saved trait answers (or a whole profile —
 * the `traits` sub-object is scanned too). Saved trait answers store option values as a string
 * or a string array per question key (see traits.ts `normalize_trait_answers` / how
 * delivery_type rides in `profile.traits`); the question is owned by traits.ts, so this helper
 * only pattern-matches the option VALUE: anything containing "difficult"/"complicated" reads
 * as a difficult birth, anything containing "easy" as an easy one. Absent key, non-string
 * values, unrecognised options, or a contradictory easy+difficult double-selection → null
 * (unknown — current ordinary-birth behaviour applies).
 */
export function delivery_ease_from_traits(answers: any): DeliveryEase | null {
  if (answers === null || answers === undefined || typeof answers !== "object" || Array.isArray(answers)) {
    return null;
  }
  let raw = (answers as Record<string, any>)["delivery_ease"];
  if (raw === undefined) {
    const traits = (answers as Record<string, any>)["traits"];
    if (traits !== null && typeof traits === "object" && !Array.isArray(traits)) {
      raw = (traits as Record<string, any>)["delivery_ease"];
    }
  }
  const values: any[] = typeof raw === "string" ? [raw] : Array.isArray(raw) ? raw : [];
  let easy = false;
  let difficult = false;
  for (const v of values) {
    if (typeof v !== "string") continue;
    const s = v.toLowerCase();
    // "uncomplicated" must not read as "complicated" — strip the negated form first.
    const stripped = s.replace(/uncomplicated/g, "");
    if (stripped.includes("difficult") || stripped.includes("complicated")) difficult = true;
    else if (s.includes("easy") || s.includes("uncomplicated")) easy = true;
  }
  if (easy === difficult) return null; // both unknown, or contradictory double-selection
  return difficult ? "difficult" : "easy";
}

/**
 * Degrees of Pranapada arc from `pp` to the NEAREST whole-sign boundary at which the
 * credited/uncredited verdict flips. Boundaries between two houses of the SAME class (e.g.
 * the auspicious 4|5 or 9|10|11 runs) are NOT flips — a small birth-time shift there changes
 * nothing, so they must not grade the credit down.
 */
export function pranapada_flip_distance_deg(
  pp: number,
  asc_sign: number,
  credited_houses: Set<number>,
): number {
  const house = _whole_sign_house_of(pp, asc_sign);
  const in_credited = credited_houses.has(house);
  const offset = pymod(_NORM(pp), 30.0); // degrees into the current whole-sign house
  let forward = 30.0 - offset;
  for (let i = 1; i < 12; i++) {
    if (credited_houses.has(((house - 1 + i) % 12) + 1) !== in_credited) break;
    forward += 30.0;
  }
  let backward = offset;
  for (let i = 1; i < 12; i++) {
    if (credited_houses.has(((house - 1 - i + 12) % 12) + 1) !== in_credited) break;
    backward += 30.0;
  }
  return Math.min(forward, backward);
}

export interface PranapadaGrade {
  house: number | null;
  /** Doc-verdict: Pranapada in the classically auspicious 2/4/5/9/10/11 (display semantics,
   *  independent of delivery ease). */
  auspicious: boolean;
  /** The house set that EARNS the credit for this profile (flips under a difficult birth). */
  credited: boolean;
  delivery_ease: DeliveryEase | null;
  /** Distance (deg of Pranapada arc) to the nearest verdict-flip boundary; null without a Sun. */
  flip_distance_deg: number | null;
  /** 0..1 graded credit: 0 in an uncredited house or AT a flip boundary, 1 when ≥ the ramp. */
  grade: number;
  /** 50..100 confirmation score (50 + 50 × grade) — never below the neutral 50. */
  score: number;
}

/**
 * GRADED, TWO-SIDED Pranapada confirmation (01_sensitive_points_timing.md "Pranapad —
 * Auspicious-Birth Sensitive Point": auspicious in the 2nd/5th/9th/4th/10th/11th from the
 * Lagna, "In any other Bhava, Pranapad indicates an inauspicious birth").
 *
 *   • TWO-SIDED: an ordinary/easy birth (delivery_ease "easy" or unknown) is expected to show
 *     Pranapada in the AUSPICIOUS houses — unchanged from the original binary check. A birth
 *     the family KNOWS was difficult/complicated is, per the doc's judgment rule, expected in
 *     the INAUSPICIOUS houses — so under delivery_ease="difficult" the credit flips to those.
 *     Additive-only either way (50..100, the neutral floor is 50): the ease answer decides
 *     WHICH candidates collect the tie-break credit, it never penalises anyone.
 *   • GRADED: within the credited class the credit scales with the distance to the nearest
 *     verdict-flip boundary (linear over PRANAPADA_GRADING_RAMP_DEG, ~2 min of birth time):
 *     deep inside → full credit; at a flip boundary → ~0. Pranapada moves one sign per ~6 min,
 *     so this gradient separates candidate seconds the binary check scored identically.
 *
 * A null `pp` (chart without a Sun) degrades to the neutral 50 — never a crash or a penalty.
 */
export function graded_pranapada_score(
  pp: number | null,
  asc_sign: number,
  delivery_ease: DeliveryEase | null = null,
): PranapadaGrade {
  if (pp === null) {
    return {
      house: null, auspicious: false, credited: false, delivery_ease,
      flip_distance_deg: null, grade: 0.0, score: 50.0,
    };
  }
  const credited_houses =
    delivery_ease === "difficult" ? PRANAPADA_INAUSPICIOUS_HOUSES : PRANAPADA_AUSPICIOUS_HOUSES;
  const house = _whole_sign_house_of(pp, asc_sign);
  const auspicious = PRANAPADA_AUSPICIOUS_HOUSES.has(house);
  const credited = credited_houses.has(house);
  const flip = pranapada_flip_distance_deg(pp, asc_sign, credited_houses);
  const grade = credited ? Math.min(1.0, flip / PRANAPADA_GRADING_RAMP_DEG) : 0.0;
  return {
    house, auspicious, credited, delivery_ease,
    flip_distance_deg: flip, grade, score: 50.0 + 50.0 * grade,
  };
}

export interface SpecialLagnas {
  hora_lagna: number;
  ghati_lagna: number;
  bhava_lagna: number;
}

/**
 * The three time-proportional special lagnas (Vol_01 §3.3), as sidereal longitudes.
 * Each starts from the Sun's position at sunrise and advances with elapsed ghatis
 * (1 ghati = 24 min): Bhava 6°/ghati (1 sign per 120 min), Hora 12°/ghati (per 60 min),
 * Ghati 30°/ghati (per 24 min).
 */
export function special_lagnas(sun_at_sunrise: number, seconds_since_sunrise: number): SpecialLagnas {
  const ghatis = seconds_since_sunrise / 1440.0;
  return {
    hora_lagna: _NORM(sun_at_sunrise + ghatis * 12.0),
    ghati_lagna: _NORM(sun_at_sunrise + ghatis * 30.0),
    bhava_lagna: _NORM(sun_at_sunrise + ghatis * 6.0),
  };
}

// The three Gandanta junctions are the water→fire sign boundaries: Pisces→Aries (0°),
// Cancer→Leo (120°), Scorpio→Sagittarius (240°) — also exactly the Revati/Ashvini,
// Aslesha/Magha, Jyeshtha/Mula nakshatra junctions (Vol_01 §6.2).
const _GANDANTA_JUNCTIONS = [0.0, 120.0, 240.0];

/** Signed distance (deg, -180..180) from a longitude to its nearest Gandanta junction. */
export function gandanta_distance(longitude: number): number {
  let best = 999.0;
  for (const j of _GANDANTA_JUNCTIONS) {
    let d = pymod(_NORM(longitude) - j + 180.0, 360.0) - 180.0;
    if (Math.abs(d) < Math.abs(best)) best = d;
  }
  return best;
}

// Gandanta zones are TIME windows the sources state in ghatikas, so each orb must be converted at
// the speed of ITS OWN point (2026-07-02 correction — the old code applied the Moon's arc to both):
//  • Lagna Gandanta (12_gandanta_births.md): the last/first HALF-GHATIKA of rising = 12 min of
//    time. The Ascendant sweeps ~360° per day ≈ 0.25°/min, so half a ghatika ≈ 3° of Ascendant arc
//    either side of the junction (matching this module's docstring and the classical convention).
//  • Moon Nakshatra Gandanta (12_gandanta_births.md): "Last 2 Ghatikas of Revati Nakshatra, OR
//    First 2 Ghatikas of Ashvini Nakshatra" (identically for Aslesha/Magha and Jyeshtha/Mula) —
//    the window is 2 GHATIKAS on each side of the junction. Converted at the source's own
//    per-ghati gloss (01_gandanta_nakshatras.md: "1 Ghati (approximately 24 arc-minutes)" of
//    Moon motion): 2 ghatikas × 24′ = 48′ ≈ 0.8° of Moon arc either side.
// (01_gandanta_nakshatras.md's "24 arc-minutes" gloss is a Moon-speed conversion; applying it to
// the fast-moving Ascendant understated the lagna window ~7×.)
export const LAGNA_GANDANTA_ORB_DEG = 3.0;
// 0.8 = 2 ghatikas × the doc's 24′/ghati (corrected 2026-07-20 — the earlier 0.4 was a
// 1-ghatika orb, half the documented window).
export const MOON_GANDANTA_ORB_DEG = 0.8;

// Tithi Gandanta (12_gandanta_births.md): "Last 2 Ghatikas of Purna Tithi (5th, 10th, or 15th
// Tithi), OR First 2 Ghatikas of Nanda Tithi (1st, 6th, or 11th Tithi)". Each tithi = 12° of
// Sun→Moon elongation; within each 15-tithi paksha the Purna tithis are 5/10/15 and the Nanda
// tithis 1/6/11, so every flagged boundary (Purna 5→Nanda 6, Purna 10→Nanda 11, Purna 15→Nanda 1
// of the next paksha) sits at an elongation MULTIPLE OF 60° (5 tithis × 12°) — six junctions:
// 0°, 60°, 120°, 180°, 240°, 300°. The window is symmetric (2 Ghatikas each side). Conversion:
// 2 Ghatikas = 48 min of TIME; the elongation grows at the Moon−Sun relative rate
// ≈ 13.18 − 0.99 ≈ 12.19°/day (mean), so 48 min ≈ (48/1440) × 12.19 ≈ 0.41° of elongation.
export const TITHI_GANDANTA_ORB_DEG = 0.41;

// Abhukta Mula (12_gandanta_births.md): "Last 6 Ghatikas of Jyeshtha Nakshatra PLUS First 8
// Ghatikas of Mula" — an ASYMMETRIC time window around the Jyeshtha/Mula junction (240°) only,
// "Most severe of ALL Gandanta periods". Converted at the Moon's own speed exactly like
// MOON_GANDANTA_ORB_DEG above (the Moon moves ~0.22°/ghatika — 01_gandanta_nakshatras.md's
// convention): 6 Ghatikas ≈ 6 × 0.22 ≈ 1.32° before the junction, 8 Ghatikas ≈ 1.76° after.
export const ABHUKTA_MULA_ORB_BEFORE_DEG = 1.32;
export const ABHUKTA_MULA_ORB_AFTER_DEG = 1.76;

/**
 * Signed distance (deg) from the Sun→Moon elongation to its nearest Tithi-Gandanta boundary
 * (the Purna→Nanda junctions — every multiple of 60° of elongation; see TITHI_GANDANTA_ORB_DEG).
 * Negative = still in the Purna tithi (before the boundary), positive = into the Nanda tithi.
 */
export function tithi_gandanta_distance(sun_longitude: number, moon_longitude: number): number {
  const elongation = pymod(_NORM(moon_longitude) - _NORM(sun_longitude), 360.0);
  return pymod(elongation + 30.0, 60.0) - 30.0;
}

/** Tithi number 1..30 from the Sun→Moon elongation (each tithi = 12°; 1 = Shukla Pratipada). */
export function tithi_number(sun_longitude: number, moon_longitude: number): number {
  const elongation = pymod(_NORM(moon_longitude) - _NORM(sun_longitude), 360.0);
  return Math.min(30, Math.trunc(elongation / 12.0) + 1);
}

export interface GandantaFlags {
  lagna_gandanta: boolean;
  /** null when the Moon longitude was not supplied (Moon flags unknowable). */
  moon_gandanta: boolean | null;
  lagna_distance_deg: number;
  moon_distance_deg: number | null;
  /** Most severe of all Gandanta periods (12_gandanta_births.md) — Moon in the asymmetric inner
   *  Jyeshtha/Mula window. null when the Moon longitude was not supplied. */
  abhukta_mula: boolean | null;
  /** Signed Moon distance (deg) from the 240° Jyeshtha/Mula junction (null without a Moon). */
  abhukta_mula_distance_deg: number | null;
  /** null when the Sun (or Moon) longitude was not supplied (tithi unknown). */
  tithi_gandanta: boolean | null;
  tithi: number | null;
  tithi_distance_deg: number | null;
}

/**
 * Gandanta boundary flags for a chart (display-only; see module docstring). A missing Moon
 * (`moon_longitude: null` — e.g. a stub chart) yields null Moon/tithi flags rather than a
 * fabricated verdict: the Moon-based windows simply cannot be testified to without a Moon.
 */
export function gandanta_flags(
  asc_longitude: number,
  moon_longitude: number | null,
  sun_longitude: number | null = null,
): GandantaFlags {
  const lagna_d = gandanta_distance(asc_longitude);
  const moon_d = moon_longitude === null ? null : gandanta_distance(moon_longitude);
  // Abhukta Mula concerns ONLY the 240° (Jyeshtha→Mula) junction, with an asymmetric orb.
  const abhukta_d =
    moon_longitude === null ? null : pymod(_NORM(moon_longitude) - 240.0 + 180.0, 360.0) - 180.0;
  const abhukta =
    abhukta_d === null
      ? null
      : abhukta_d >= -ABHUKTA_MULA_ORB_BEFORE_DEG && abhukta_d <= ABHUKTA_MULA_ORB_AFTER_DEG;
  let tithi_flag: boolean | null = null;
  let tithi: number | null = null;
  let tithi_d: number | null = null;
  if (sun_longitude !== null && moon_longitude !== null) {
    tithi_d = tithi_gandanta_distance(sun_longitude, moon_longitude);
    tithi = tithi_number(sun_longitude, moon_longitude);
    tithi_flag = Math.abs(tithi_d) <= TITHI_GANDANTA_ORB_DEG;
  }
  return {
    lagna_gandanta: Math.abs(lagna_d) <= LAGNA_GANDANTA_ORB_DEG,
    moon_gandanta: moon_d === null ? null : Math.abs(moon_d) <= MOON_GANDANTA_ORB_DEG,
    lagna_distance_deg: pythonRound(lagna_d, 2),
    moon_distance_deg: moon_d === null ? null : pythonRound(moon_d, 2),
    abhukta_mula: abhukta,
    abhukta_mula_distance_deg: abhukta_d === null ? null : pythonRound(abhukta_d, 2),
    tithi_gandanta: tithi_flag,
    tithi,
    tithi_distance_deg: tithi_d === null ? null : pythonRound(tithi_d, 2),
  };
}

// ── Kala Vela upagrahas (01_sensitive_points_timing.md) — display-only ──────────────────────
// The DAY duration (sunrise→sunset) is split into 8 equal parts; parts 1-7 belong to the seven
// planets in vaara (weekday) order STARTING from the day's weekday lord, the 8th is lord-less.
// For NIGHT births the sunset→next-sunrise span is split the same way, the sequence starting
// from the lord of the 5th weekday counted (inclusively) from the birth weekday. Each
// upagraha's longitude is the ASCENDANT degree rising at the START of its planet's portion.

/** Vaara (weekday-lord) order — index 0 = Sunday's lord. */
export const VAARA_SEQUENCE = ["sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn"] as const;

/** Which planet's Kala Vela portion each upagraha takes (01_sensitive_points_timing.md). */
export const UPAGRAHA_PORTION_LORDS: Record<string, string> = {
  gulik: "saturn",
  kaal: "sun",
  mrityu: "mars",
  ardhaprahar: "mercury",
  yamaghantak: "jupiter",
};

/**
 * Portion-START JDs of the five upagrahas for a day/night span split into 8 equal parts,
 * the planetary sequence beginning at `start_lord_index` (0=Sun..6=Saturn in VAARA_SEQUENCE).
 * Pure arithmetic — the caller supplies sunrise→sunset (day) or sunset→next-sunrise (night).
 */
export function kala_vela_portion_starts(
  span_start_jd: number,
  span_end_jd: number,
  start_lord_index: number,
): Record<string, number> {
  const part = (span_end_jd - span_start_jd) / 8.0;
  const out: Record<string, number> = {};
  for (const [name, lord] of Object.entries(UPAGRAHA_PORTION_LORDS)) {
    // Portion i (0-based, i<7) is lorded by VAARA_SEQUENCE[(start+i) % 7]; the 8th is lord-less.
    const portion = pymod(VAARA_SEQUENCE.indexOf(lord as (typeof VAARA_SEQUENCE)[number]) - start_lord_index, 7);
    out[name] = span_start_jd + portion * part;
  }
  return out;
}

// Sunset / next-sunrise found FROM a given JD. Like sunrise these depend only on the date+place,
// but scans hit them for every candidate second — memoise per (start JD, event, place); the
// cache stays tiny (a couple of entries per scanned day). The RISE case delegates to kp.ts's
// sunrise_jd_ut so the disc-center/no-refraction convention lives in ONE place; only the SET
// case issues its own rise_trans (same bits, swe.CALC_SET).
const _sun_event_cache = new Map<string, number | null>();

function _cached_sun_event(from_jd: number, rsmi_calc: number, lat: number, lon: number): number | null {
  const key = `${from_jd.toFixed(8)}|${rsmi_calc}|${lat}|${lon}`;
  if (!_sun_event_cache.has(key)) {
    let found: number | null = null;
    if (rsmi_calc === swe.CALC_RISE) {
      found = sunrise_jd_ut(from_jd, lat, lon);
    } else {
      try {
        const [res, tret] = swe.rise_trans(
          from_jd, swe.SUN, rsmi_calc | swe.BIT_DISC_CENTER | swe.BIT_NO_REFRACTION, [lon, lat, 0.0],
        );
        if (res === 0) found = tret[0];
      } catch {
        found = null;
      }
    }
    _sun_event_cache.set(key, found);
  }
  return _sun_event_cache.get(key)!;
}

/**
 * Sidereal Ascendant at a JD — the same swe.houses_ex call (and Placidus→Whole-Sign polar
 * fallback) the natal chart uses in src/astro/chart.ts; the Ascendant itself is identical
 * under both systems.
 */
function _asc_at_jd(jd: number, lat: number, lon: number): number {
  try {
    const [, ascmc] = swe.houses_ex(jd, lat, lon, "P", swe.FLG_SIDEREAL);
    return _NORM(ascmc[0]);
  } catch {
    const [, ascmc] = swe.houses_ex(jd, lat, lon, "W", swe.FLG_SIDEREAL);
    return _NORM(ascmc[0]);
  }
}

export interface UpagrahaPoint {
  /** JD(UT) of the START of the planet's portion — the moment whose rising degree is taken. */
  portion_start_jd: number;
  /** Sidereal longitude = the Ascendant degree at portion_start_jd. */
  longitude: number;
  /** Sign index 0-11. */
  sign: number;
}

export interface UpagrahaSet {
  available: boolean;
  /** true = born between sunrise and sunset (day scheme); false = night scheme. */
  day_birth: boolean | null;
  /** gulik / kaal / mrityu / ardhaprahar / yamaghantak. */
  points: Record<string, UpagrahaPoint> | null;
}

// The five upagrahas depend only on the Vedic day (date+place+day/night half), NOT the
// candidate second — memoise the whole set per (sunrise, place, half) like sunrise itself,
// so a scan pays the 5 houses_ex + 2 rise_trans calls once per scanned day.
const _upagraha_cache = new Map<string, UpagrahaSet>();

/**
 * The five Kala Vela upagrahas for the Vedic day (and day/night half) a birth moment falls
 * in (01_sensitive_points_timing.md). Returns available:false at polar latitudes or on
 * ephemeris failure — callers then omit the display block.
 */
export function upagrahas_for_moment(
  birth_dt: PlainDateTime,
  lat: number,
  lon: number,
  timezone_offset: number,
): UpagrahaSet {
  const none: UpagrahaSet = { available: false, day_birth: null, points: null };
  try {
    const anchor = sunrise_anchor(birth_dt, lat, lon, timezone_offset);
    if (!anchor.available) return none;
    const rise = anchor.sunrise_jd!;
    const birth_jd = rise + anchor.seconds_since_sunrise! / 86400.0;
    const sunset = _cached_sun_event(rise, swe.CALC_SET, lat, lon);
    if (sunset === null) return none;
    const day_birth = birth_jd < sunset;

    const cache_key = `${rise.toFixed(8)}|${lat}|${lon}|${day_birth ? "day" : "night"}`;
    const cached = _upagraha_cache.get(cache_key);
    if (cached !== undefined) return cached;

    // Vaara index (0=Sun..6=Saturn) of the Vedic day: datetime.weekday is 0=Mon..6=Sun.
    const vaara_idx = pymod(weekday(anchor.vedic_date!) + 1, 7);
    let span_start: number;
    let span_end: number;
    let start_lord: number;
    if (day_birth) {
      span_start = rise;
      span_end = sunset;
      start_lord = vaara_idx;
    } else {
      const next_rise = _cached_sun_event(sunset, swe.CALC_RISE, lat, lon);
      if (next_rise === null) return none;
      span_start = sunset;
      span_end = next_rise;
      // Night sequence starts from the lord of the 5TH weekday counted from the birth
      // weekday (inclusive): Thursday → Thu(1) Fri(2) Sat(3) Sun(4) Mon(5) → Moon.
      start_lord = pymod(vaara_idx + 4, 7);
    }

    const starts = kala_vela_portion_starts(span_start, span_end, start_lord);
    const points: Record<string, UpagrahaPoint> = {};
    for (const [name, jd] of Object.entries(starts)) {
      const longitude = _asc_at_jd(jd, lat, lon);
      points[name] = { portion_start_jd: jd, longitude, sign: Math.trunc(longitude / 30.0) };
    }
    const result: UpagrahaSet = { available: true, day_birth, points };
    _upagraha_cache.set(cache_key, result);
    return result;
  } catch {
    return none;
  }
}

// ── Varnad Lagna (10_varnad_lagna.md) — display-only ─────────────────────────────────────────

/**
 * Varnad Lagna sign (0-11) from the natal Lagna and Hora Lagna signs (10_varnad_lagna.md; the
 * old "repeat the count from Hora Lagna" method is explicitly rejected there):
 *   L/H: ODD sign → count forward from Aries (inclusive); EVEN sign → count in REVERSE from
 *        Pisces (Pisces=1 … Aries=12).
 *   N:   same parity → L+H, mixed parity → |L−H|.
 *   VL:  N odd → count N forward from Aries; N even → count N in reverse from Pisces
 *        (parity of N itself, not N mod 12, picks the direction when N > 12).
 * Note: inclusive counting makes BOTH products odd for every sign (odd signs sit at odd
 * forward positions, even signs at odd reverse positions), so in practice N is always even
 * (reverse-from-Pisces). The mixed-parity/odd-N branches — and the defensive N=0→12
 * convention (unreachable: mixed parity forces L≠H) — are kept for fidelity to the doc.
 */
export function varnad_lagna(natal_lagna_sign: number, hora_lagna_sign: number): number {
  // Inclusive count: odd sign (even index) forward from Aries = idx+1; even sign reverse
  // from Pisces = 12-idx (Pisces=1, Aquarius=2, … Aries=12).
  const count_of = (sign: number): number => {
    const idx = pymod(Math.trunc(sign), 12);
    return idx % 2 === 0 ? idx + 1 : 12 - idx;
  };
  const l = count_of(natal_lagna_sign);
  const h = count_of(hora_lagna_sign);
  let n = l % 2 === h % 2 ? l + h : Math.abs(l - h);
  if (n === 0) n = 12; // unreachable defensive convention (see docstring)
  // N forward from Aries lands on index N-1; N reverse from Pisces on index 12-N (mod 12).
  return n % 2 === 1 ? pymod(n - 1, 12) : pymod(12 - n, 12);
}

export interface SpecialPointsResult {
  available: boolean;
  /** 50–100 confirmation score (Pranapada in an auspicious house lifts toward 100). */
  score: number | null;
  detail: Record<string, any>;
}

const SIGN_NAMES = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];

/**
 * Per-candidate secondary-checks bundle: the Pranapada confirmation score plus the special
 * lagna positions, Gandanta flags, Kala Vela upagrahas and Varnad Lagna for display. The
 * score is the GRADED, TWO-SIDED Pranapada credit (see graded_pranapada_score — Vol_01 §9.3 /
 * 01_sensitive_points_timing.md "Pranapad"): 50..100, full 100 only deep inside a credited
 * house, falling toward the neutral 50 near a verdict-flip boundary, and NEVER below 50 — so
 * per PLAN §6 it can corroborate a birth time but never veto one. `trait_answers` (optional,
 * the profile's saved trait answers) supplies the `delivery_ease` birth fact: a known
 * difficult/complicated delivery flips WHICH house class earns the credit (the doc's
 * inauspicious houses); absent/unknown keeps the classical ordinary-birth expectation.
 * The upagrahas and Varnad Lagna are DISPLAY-ONLY (like the Gandanta flags): their
 * houses/signs flip with the candidate rising sign but never move the score.
 */
export function score_special_points_for_candidate(
  natal_chart: RawChartData,
  birth_dt: PlainDateTime,
  birth_info: Record<string, any>,
  timezone_offset: number,
  trait_answers: Record<string, any> | null = null,
): SpecialPointsResult {
  const lat = Number(birth_info["lat"]);
  const lon = Number(birth_info["lon"]);
  const anchor = sunrise_anchor(birth_dt, lat, lon, timezone_offset);
  if (!anchor.available) {
    return { available: false, score: null, detail: { reason: "no sunrise (polar/ephemeris)" } };
  }

  const sun = natal_chart.planets["sun"];
  const moon = natal_chart.planets["moon"];
  const asc_sign = Math.trunc(_NORM(natal_chart.ascendant_longitude) / 30.0);

  // Pranapada needs the natal Sun (Vol_01 §9.3 starts FROM the Sun). A chart without a Sun
  // (synthetic stub) degrades to null pranapada fields and the neutral 50 — never a crash,
  // never a penalty (same degrade pattern as the missing-Moon/missing-Sun gandanta flags).
  const pp = sun ? pranapada_longitude(Number(sun.longitude), anchor.seconds_since_sunrise!) : null;
  // Graded, two-sided credit (see graded_pranapada_score): the profile's delivery_ease trait
  // (read defensively — absent/unknown → null → classical ordinary-birth expectation) picks
  // which house class earns the bonus; the distance to the nearest verdict-flip boundary
  // grades it. 50..100 always — additive-only downstream.
  const ease = delivery_ease_from_traits(trait_answers);
  const pranapada = graded_pranapada_score(pp, asc_sign, ease);
  const pp_house = pranapada.house;
  const auspicious = pranapada.auspicious;

  const lagnas = special_lagnas(anchor.sun_at_sunrise!, anchor.seconds_since_sunrise!);
  const flags = gandanta_flags(
    natal_chart.ascendant_longitude,
    // A missing Moon yields null Moon flags (it previously fed the ASCENDANT longitude in as
    // the "Moon", fabricating Moon-gandanta verdicts from the wrong point; fixed 2026-07-20).
    moon ? Number(moon.longitude) : null,
    // Sun enables the Tithi-Gandanta flag (12_gandanta_births.md) — display-only like the rest.
    sun ? Number(sun.longitude) : null,
  );

  // Kala Vela upagrahas (01_sensitive_points_timing.md) — display-only. The longitudes are
  // fixed for the whole Vedic day-half (memoised), but the HOUSE each occupies from the
  // candidate Ascendant is the birth-time-sensitive part; Gulik in the Lagna ("Gulik
  // afflicting the Ascendant": physical suffering) is the classically flagged case.
  const upa = upagrahas_for_moment(birth_dt, lat, lon, timezone_offset);
  let upagrahas: Record<string, any> | null = null;
  let gulika_in_lagna: boolean | null = null;
  if (upa.available && upa.points !== null) {
    upagrahas = { day_birth: upa.day_birth };
    for (const [name, point] of Object.entries(upa.points)) {
      upagrahas[name] = {
        longitude: pythonRound(point.longitude, 2),
        sign: SIGN_NAMES[point.sign],
        house_from_lagna: _whole_sign_house_of(point.longitude, asc_sign),
      };
    }
    gulika_in_lagna = upagrahas["gulik"]["house_from_lagna"] === 1;
  }

  // Varnad Lagna (10_varnad_lagna.md) — display-only, from the natal Lagna + Hora Lagna signs.
  const varnad = varnad_lagna(asc_sign, Math.trunc(lagnas.hora_lagna / 30.0));

  return {
    available: true,
    score: pranapada.score,
    detail: {
      pranapada_longitude: pp === null ? null : pythonRound(pp, 2),
      pranapada_sign: pp === null ? null : SIGN_NAMES[Math.trunc(pp / 30.0)],
      pranapada_house: pp_house,
      pranapada_auspicious: auspicious,
      // Graded two-sided credit fields (01_sensitive_points_timing.md "Pranapad").
      pranapada_credited: pranapada.credited,
      pranapada_grade: pythonRound(pranapada.grade, 4),
      pranapada_flip_distance_deg:
        pranapada.flip_distance_deg === null ? null : pythonRound(pranapada.flip_distance_deg, 2),
      delivery_ease: ease,
      hora_lagna_sign: SIGN_NAMES[Math.trunc(lagnas.hora_lagna / 30.0)],
      ghati_lagna_sign: SIGN_NAMES[Math.trunc(lagnas.ghati_lagna / 30.0)],
      bhava_lagna_sign: SIGN_NAMES[Math.trunc(lagnas.bhava_lagna / 30.0)],
      ghati_lagna_longitude: pythonRound(lagnas.ghati_lagna, 2),
      hora_lagna_longitude: pythonRound(lagnas.hora_lagna, 2),
      bhava_lagna_longitude: pythonRound(lagnas.bhava_lagna, 2),
      gandanta: flags,
      upagrahas,
      gulika_in_lagna,
      varnad_lagna: SIGN_NAMES[varnad],
    },
  };
}

// ── Ghati-Lagna event confirmation (Vol_01 §3.3: power, status, fame) ────────────────────────
// "Keep all natal planetary positions fixed, but rebuild the house structure using each special
// Lagna as the first house." For a status/fame event we check whether the dasha lords running
// at the event date OCCUPY or LORD the event's primary houses counted from the GHATI LAGNA.
// The Ghati Lagna advances one sign every 24 minutes, so this check separates birth times
// several times WITHIN a single rising sign — resolution the natal-lagna tests can't reach.

/** Categories the Ghati Lagna governs (power/status/fame per Vol_01 §3.3). */
export const GHATI_LAGNA_CATEGORIES = new Set([
  "career_breakthrough", "career_contract", "career_failure", "award",
]);

/**
 * 50–100 confirmation: does a running dasha lord activate the event's houses in the
 * Ghati-Lagna chart (occupant 100 / house lord 85 / neither 50)? Returns null when the
 * category isn't Ghati-Lagna-governed or inputs are missing — no component, never a penalty.
 */
export function score_ghati_lagna_for_event(
  natal_chart: RawChartData,
  ghati_lagna_longitude: number,
  category: string,
  houses: number[],
  dasha_lords: Array<string | null | undefined>,
): [number | null, Record<string, any> | null] {
  if (!GHATI_LAGNA_CATEGORIES.has(String(category || "").trim().toLowerCase())) return [null, null];
  if (!houses.length) return [null, null];
  const gl_sign = Math.trunc(_NORM(ghati_lagna_longitude) / 30.0);
  const lords = dasha_lords.filter((l): l is string => !!l).map((l) => l.toLowerCase());
  if (!lords.length) return [null, null];

  const house_signs = houses.map((h) => pymod(gl_sign + h - 1, 12));
  let best = 50.0;
  const hits: Record<string, string> = {};
  for (const lord of lords) {
    const planet = natal_chart.planets[lord];
    if (planet !== undefined && house_signs.includes(Math.trunc(_NORM(Number(planet.longitude)) / 30.0))) {
      best = Math.max(best, 100.0);
      hits[lord] = "occupies";
      continue;
    }
    if (house_signs.some((s) => SIGN_RULERS[s] === lord)) {
      best = Math.max(best, 85.0);
      if (!(lord in hits)) hits[lord] = "lords";
    }
  }
  return [
    best,
    {
      ghati_lagna_sign: SIGN_NAMES[gl_sign],
      houses_from_ghati_lagna: houses,
      dasha_lords: lords,
      hits,
      score: pythonRound(best, 1),
    },
  ];
}

// ── Hora-Lagna wealth confirmation (01_ascendant_lagna.md) ───────────────────────────────────
// "Used exclusively for wealth assessment ... Kendras and Trikonas from Hora Lagna are the most
// favorable positions for wealth. Timing: The Dasha of planets placed in Kendras/Trikonas from
// Hora Lagna correlates with periods of financial gain." And 01_sensitive_points_timing.md:
// "prepare a separate Bhava Kundali for each special Lagna and analyze it exactly as the natal
// Lagna chart." Mirrors the Ghati-Lagna check above; the Hora Lagna advances one sign every
// 60 minutes, so it too separates birth times WITHIN a single rising sign.

/**
 * Categories the Hora Lagna governs — the MONEY-OUTCOME event categories (wealth acquisition/
 * financial gain). Award/career stay with the Ghati Lagna (status/power/fame) per Vol_01 §3.3;
 * the two category sets are DISJOINT, so kp_scoring routes the single special_lagna component
 * by category (no averaging needed).
 */
export const HORA_LAGNA_CATEGORIES = new Set(["property_purchase", "social_celebration"]);

/** Kendra (1/4/7/10) + Trikona (1/5/9) houses — the doc's favourable wealth positions. */
const _KENDRA_TRIKONA_HOUSES = [1, 4, 5, 7, 9, 10];

/**
 * 50–100 confirmation for WEALTH events: does a running dasha lord sit in a Kendra/Trikona
 * FROM THE HORA LAGNA (occupant 100 / lord of such a sign 85 / neither 50)? Returns null when
 * the category isn't Hora-Lagna-governed or inputs are missing — no component, never a penalty.
 */
export function score_hora_lagna_for_event(
  natal_chart: RawChartData,
  hora_lagna_longitude: number,
  category: string,
  dasha_lords: Array<string | null | undefined>,
): [number | null, Record<string, any> | null] {
  if (!HORA_LAGNA_CATEGORIES.has(String(category || "").trim().toLowerCase())) return [null, null];
  const hl_sign = Math.trunc(_NORM(hora_lagna_longitude) / 30.0);
  const lords = dasha_lords.filter((l): l is string => !!l).map((l) => l.toLowerCase());
  if (!lords.length) return [null, null];

  const favourable_signs = _KENDRA_TRIKONA_HOUSES.map((h) => pymod(hl_sign + h - 1, 12));
  let best = 50.0;
  const hits: Record<string, string> = {};
  for (const lord of lords) {
    const planet = natal_chart.planets[lord];
    if (planet !== undefined && favourable_signs.includes(Math.trunc(_NORM(Number(planet.longitude)) / 30.0))) {
      best = Math.max(best, 100.0);
      hits[lord] = "occupies";
      continue;
    }
    if (favourable_signs.some((s) => SIGN_RULERS[s] === lord)) {
      best = Math.max(best, 85.0);
      if (!(lord in hits)) hits[lord] = "lords";
    }
  }
  return [
    best,
    {
      hora_lagna_sign: SIGN_NAMES[hl_sign],
      kendra_trikona_houses: _KENDRA_TRIKONA_HOUSES,
      dasha_lords: lords,
      hits,
      score: pythonRound(best, 1),
    },
  ];
}
